import { signOut } from "@internal/auth";
import { ChevronRight } from "lucide-react";
import { useState } from "react";
import { MobileShell } from "@/components/layout/MobileShell";
import { Card } from "@/components/ui/Card";
import { Sparkline } from "@/components/ui/Sparkline";
import { WeekSummaryCard } from "@/components/ui/WeekSummaryCard";
import { useObservable } from "@/hooks/useObservable";
import { useProfile } from "@/hooks/useProfile";
import {
  computeWeekFinancials,
  findPriorValues,
  sumLogAmounts,
} from "@/lib/weekFinancials";
import { weekLogService } from "@/services/weekLog.service";
import type { JobLogRecord, WeekRecord, WeekStatus } from "@/types/domain";

const STATUS_LABEL: Record<WeekStatus, string> = {
  open: "Pågår",
  child_confirmed: "Venter på foreldre",
  closed: "Fullført",
};

export function ChildHistory() {
  const { profile } = useProfile();
  const weekLogState = useObservable(weekLogService.state$, {
    week: null,
    logs: [],
    siblingTotals: {},
    isPreviousWeek: false,
    pendingPreviousWeek: null,
    allWeeks: [],
    allLogs: [],
    loading: false,
  });
  const [expandedId, setExpandedId] = useState<string | null>(null);

  if (!profile) return null;

  const sortedWeeks = [...weekLogState.allWeeks].sort((a, b) =>
    a.data.year !== b.data.year
      ? b.data.year - a.data.year
      : b.data.iso_week - a.data.iso_week,
  );

  const closedWeeks = sortedWeeks.filter((w) => w.data.status === "closed");
  const last6Closed = closedWeeks.slice(0, 6);
  const last6Totals = last6Closed.map((w) => {
    const logs = weekLogState.allLogs.filter((l) => l.data.week_id === w.id);
    return sumLogAmounts(logs);
  });
  const totalLast6 = last6Totals.reduce((s, n) => s + n, 0);

  return (
    <MobileShell>
      <div style={{ padding: "52px 20px 20px" }}>
        <div style={{ marginBottom: 16 }}>
          <div
            style={{
              fontSize: 12,
              color: "var(--ink-3)",
              textTransform: "uppercase",
              letterSpacing: 0.7,
              fontWeight: 700,
            }}
          >
            Arkiv
          </div>
          <div
            style={{
              fontFamily: "var(--font-display)",
              fontStyle: "italic",
              fontSize: 30,
              fontWeight: 400,
              marginTop: 2,
              letterSpacing: -0.6,
            }}
          >
            Tidligere uker
          </div>
        </div>

        <Card
          data-testid="history-hero-card"
          style={{ padding: 16, marginBottom: 14 }}
        >
          <div
            style={{
              fontSize: 12,
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: 0.7,
              color: "var(--ink-3)",
            }}
          >
            Siste 6 uker
          </div>
          <div
            className="num"
            data-testid="history-hero-total"
            style={{
              fontSize: 28,
              fontWeight: 700,
              letterSpacing: -1,
              marginTop: 2,
            }}
          >
            +{totalLast6} kr
          </div>
          {last6Totals.length > 0 && (
            <div style={{ marginTop: 10 }}>
              <Sparkline
                data={[...last6Totals].reverse()}
                width={310}
                height={50}
                color="var(--good)"
              />
            </div>
          )}
        </Card>

        {sortedWeeks.length === 0 ? (
          <div
            data-testid="history-empty"
            style={{
              textAlign: "center",
              padding: "24px 0",
              color: "var(--ink-3)",
              fontSize: 14,
            }}
          >
            Ingen uker enda
          </div>
        ) : (
          <Card padding={0} style={{ overflow: "hidden" }}>
            {sortedWeeks.map((w, i) => (
              <HistoryWeekRow
                key={w.id}
                week={w}
                allLogsForWeek={weekLogState.allLogs.filter(
                  (l) => l.data.week_id === w.id,
                )}
                allWeeksForPriors={weekLogState.allWeeks}
                userId={profile.id}
                expanded={expandedId === w.id}
                onToggle={() =>
                  setExpandedId(expandedId === w.id ? null : w.id)
                }
                isLast={i === sortedWeeks.length - 1}
              />
            ))}
          </Card>
        )}

        <button
          type="button"
          onClick={() => signOut()}
          style={{
            marginTop: 24,
            width: "100%",
            padding: "12px 0",
            borderRadius: 12,
            fontSize: 14,
            fontWeight: 600,
            color: "var(--ink-3)",
            background: "none",
            border: "1px solid var(--line-2)",
          }}
        >
          Logg ut
        </button>
      </div>
    </MobileShell>
  );
}

function HistoryWeekRow({
  week,
  allLogsForWeek,
  allWeeksForPriors,
  userId,
  expanded,
  onToggle,
  isLast,
}: {
  week: WeekRecord;
  allLogsForWeek: JobLogRecord[];
  allWeeksForPriors: WeekRecord[];
  userId: string;
  expanded: boolean;
  onToggle: () => void;
  isLast: boolean;
}) {
  const isClosed = week.data.status === "closed";
  const earned = sumLogAmounts(allLogsForWeek);

  const priors = findPriorValues(
    allWeeksForPriors,
    userId,
    week.data.iso_week,
    week.data.year,
  );
  const financials = computeWeekFinancials({
    earned,
    bankAfter: week.data.bank_balance_after,
    stockAfter: week.data.stock_value_after,
    totalInvested: week.data.total_invested,
    bankBefore: priors.bankBefore,
    stockBefore: priors.stockBefore,
    totalInvestedBefore: priors.totalInvestedBefore,
  });

  return (
    <div
      data-testid="history-week-row"
      data-week-status={week.data.status}
      style={{
        borderBottom: isLast ? "none" : "1px solid var(--line)",
      }}
    >
      <button
        type="button"
        data-testid="history-week-header"
        disabled={!isClosed}
        onClick={isClosed ? onToggle : undefined}
        style={{
          display: "flex",
          alignItems: "center",
          padding: "14px 16px",
          width: "100%",
          textAlign: "left",
          background: "transparent",
          cursor: isClosed ? "pointer" : "default",
        }}
      >
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 14, fontWeight: 700 }}>
            Uke {week.data.iso_week}
          </div>
          <div style={{ fontSize: 12, color: "var(--ink-3)" }}>
            {week.data.week_range_label}
            {!isClosed && (
              <>
                {" · "}
                <span data-testid="history-week-status">
                  {STATUS_LABEL[week.data.status]}
                </span>
              </>
            )}
          </div>
        </div>
        <div style={{ textAlign: "right", marginRight: isClosed ? 12 : 0 }}>
          <div
            className="num"
            style={{
              fontSize: 14,
              fontWeight: 700,
              color: "var(--good)",
            }}
          >
            +{earned} kr
          </div>
        </div>
        {isClosed && (
          <div
            style={{
              transform: expanded ? "rotate(90deg)" : "rotate(0deg)",
              transition: "transform 120ms",
              display: "flex",
            }}
          >
            <ChevronRight size={14} color="var(--ink-3)" />
          </div>
        )}
      </button>
      {isClosed && expanded && (
        <div
          data-testid="history-week-expanded"
          style={{ padding: "0 16px 16px" }}
        >
          <WeekSummaryCard financials={financials} />
        </div>
      )}
    </div>
  );
}
